import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { normalizeImageUrl } from '../utils/urlSanitizer';
import './ProductMediaGallery.css';

// items: [{ type: 'image'|'video', src, alt? }]
// onSelect: (item, index) => void
export default function ProductMediaGallery({ items = [], initialIndex = 0, onSelect }) {
  // Deduplicate sources; memoize to avoid identity churn each render
  const deduped = useMemo(() => {
    const out = [];
    const seen = new Set();
    for (const it of items) {
      const src = (it?.src || '').trim();
      if (!src || seen.has(src)) continue;
      seen.add(src);
      out.push(it);
    }
    return out;
  }, [items]);

  const [active, setActive] = useState(() => Math.min(initialIndex, Math.max(deduped.length - 1, 0)));
  const trackRef = useRef(null);

  // Touch swipe tracking
  const touchStartXRef = useRef(0);
  const touchStartYRef = useRef(0);
  const isTouchActiveRef = useRef(false);

  // Keep latest onSelect in a ref to avoid effect re-running on prop identity changes
  const onSelectRef = useRef(onSelect);
  useEffect(() => { onSelectRef.current = onSelect; }, [onSelect]);

  // Inform parent only when active index changes (prevents render loop)
  useEffect(() => {
    const item = deduped[active];
    if (item && onSelectRef.current) onSelectRef.current(item, active);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally excludes 'deduped': only 'active' should trigger this (see comment above)
  }, [active]);

  // Non-passive wheel listener to allow preventDefault and keep scroll within the column
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    const onWheel = (e) => {
      try {
        const hasVOverflow = el.scrollHeight > el.clientHeight;
        const hasHOverflow = el.scrollWidth > el.clientWidth;
        if (hasHOverflow && !hasVOverflow) {
          // Horizontal mode (mobile): map wheel to horizontal scroll
          const delta = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
          el.scrollLeft += delta;
        } else {
          // Vertical mode (desktop): default to vertical scroll
          el.scrollTop += e.deltaY;
        }
        e.preventDefault();
      } catch {}
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      try { el.removeEventListener('wheel', onWheel, { passive: false }); } catch {}
    };
  }, [trackRef]);

  const handleKey = useCallback((e) => {
    if (e.key === 'ArrowDown') {
      setActive(a => Math.min(a + 1, deduped.length - 1));
    } else if (e.key === 'ArrowUp') {
      setActive(a => Math.max(a - 1, 0));
    }
  }, [deduped.length]);

  // Touch swipe handlers for main preview
  const handleTouchStart = useCallback((e) => {
    if (deduped.length <= 1) return;
    const touch = e.touches[0];
    touchStartXRef.current = touch.clientX;
    touchStartYRef.current = touch.clientY;
    isTouchActiveRef.current = true;
  }, [deduped.length]);

  const handleTouchMove = useCallback((e) => {
    if (!isTouchActiveRef.current) return;
    const touch = e.touches[0];
    const deltaX = touch.clientX - touchStartXRef.current;
    const deltaY = touch.clientY - touchStartYRef.current;
    
    // Only prevent if horizontal swipe is dominant
    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      e.preventDefault();
    }
  }, []);

  const handleTouchEnd = useCallback((e) => {
    if (!isTouchActiveRef.current) return;
    isTouchActiveRef.current = false;
    
    const touch = e.changedTouches[0];
    const deltaX = touch.clientX - touchStartXRef.current;
    const deltaY = touch.clientY - touchStartYRef.current;
    
    // Check if horizontal swipe is dominant and exceeds threshold
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50) {
      if (deltaX > 0) {
        // Swipe right - go to previous image
        setActive(a => Math.max(a - 1, 0));
      } else {
        // Swipe left - go to next image
        setActive(a => Math.min(a + 1, deduped.length - 1));
      }
    }
  }, [deduped.length]);

  if (!deduped.length) return null;
  const current = deduped[active];
  // Render all thumbnails; container height limited so only 5 are visible at once (scroll reveal).
  const thumbItems = deduped;

  return (
    <div className='pmg-layout'>
      {thumbItems.length > 0 && (
        <div
          className='pmg-vertical-col'
          ref={trackRef}
          tabIndex={0}
          onKeyDown={handleKey}
          onMouseEnter={() => { try { trackRef.current && trackRef.current.focus(); } catch {} }}
          aria-label='Additional product images'
        >
          {thumbItems.map((it, realIdx) => {
            return (
              <button
                key={realIdx}
                type='button'
                data-pmg-idx={realIdx}
                className={`pmg-thumb-vert ${realIdx === active ? 'active' : ''}`}
                aria-current={realIdx === active ? 'true' : undefined}
                aria-label={`Thumbnail ${realIdx + 1}`}
                onClick={() => setActive(realIdx)}
              >
                {it.type === 'video' ? (
                  <div className='pmg-video-icon' />
                ) : (
                  <img
                    src={normalizeImageUrl(it.src)}
                    alt={it.alt || ''}
                    loading='lazy'
                  />
                )}
              </button>
            );
          })}
        </div>
      )}
      <div 
        className='pmg-preview-wrap'
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{ touchAction: 'pan-y' }}
      >
        {current.type === 'video' ? (
          <iframe
            src={current.src}
            allow='autoplay'
            title='product-video'
          />
        ) : (
          <img
            className='pmg-preview'
            src={normalizeImageUrl(current.src)}
            alt={current.alt || 'Product image'}
            loading='eager'
          />
        )}
      </div>
    </div>
  );
}
